import { PROJECT_LIST_SIDE_GUTTER } from './lists/project-list.constants';
import { useProjectOpening } from './project-opening.store';
import { getProjectMapLayout } from './helpers/project-map-layout.helpers';
import { useKeydownListener } from '@client/hooks/use-events.hooks';
import {
  useProjectSearchString,
  useInitProjectsSortFilter,
  useSortedProjects,
} from '@client/hooks/use-filter-projects.hooks';
import { useDeferMount } from '@client/hooks/use-defer-mount.hooks';
import { useProjectsForOverview } from '@client/store/project';
import { useIsProjectList } from '@client/hooks/use-router.hooks';
import { ProjectListTree } from './lists/ProjectListTree';
import {
  ELEMENT_LIST_WIDTH_VAR,
  ELEMENT_LIST_WIDTH_CSS,
  ELEMENT_LIST_RESIZE_HANDLE_CLASS,
  useElementListColumnWidth,
  useElementListColumnResizeHandle,
  ListColumnResizeProvider,
  projectListColumnResize,
} from '@features/proposals/ElementListRow/element-list-column-resize.hook';
import { SharedIntersectionObserverProvider } from '@client/hooks/use-intersection-observer.hooks';
import EmptyState from '@ui/EmptyState';
import { GhostButton } from '@ui/GhostButton';
import { useSearchParams } from 'react-router-dom';
import { Box, GlobalStyles } from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import { AppBarStyles } from '@client/style/style.constants';
import { ProjectListViewTabs } from './ProjectListViewTabs';
import { CreateProjectMenu } from './CreateProjectMenu';
import { PROJECT_LIST_CONTAINER_NAME } from './lists/helpers/project-list-columns.helpers';
import { ProjectListMap } from './ProjectListMap';
import {
  ProjectListViewProvider,
  useProjectListView,
} from './project-list-view.context';
import { VerticalFadeScrollbar } from '@ui/layout/MainLayoutScrollbars';

/**
 * Heavy content: tree build, sort/filter, and list. Mounted after first paint so the view opens instantly.
 */
const ProjectSelectorContent: React.FC = () => {
  const { classes } = useStyles();
  const columnWidth = useElementListColumnWidth();
  const contentRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight - AppBarStyles.HEIGHT,
  });
  useEffect(() => {
    const page = contentRef.current?.closest('[data-project-list-page]');
    if (!page) return;
    const measure = () =>
      setViewport({ width: page.clientWidth, height: page.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(page);
    return () => observer.disconnect();
  }, []);
  const handleDragStart = useElementListColumnResizeHandle();
  const projects = useProjectsForOverview();
  const { view } = useProjectListView();
  useInitProjectsSortFilter();
  const sortedFilteredProjects = useSortedProjects();
  const mapLayout = getProjectMapLayout(
    viewport.width,
    viewport.height,
    sortedFilteredProjects.length,
  );
  const listWidth = view === 'Map' ? mapLayout.width : columnWidth;
  const [, setSearchParams] = useSearchParams();
  const clearFilters = () => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete('p-search');
        next.delete('p-labels');
        next.delete('p-owner');
        next.delete('p-programs');
        return next;
      },
      { replace: true },
    );
  };
  const hasProjects = projects.length > 0;
  const hasMatchingProjects = sortedFilteredProjects.length > 0;

  if (!hasProjects) {
    // No left column: center the CTA in the full viewport over the map.
    return (
      <Box
        ref={contentRef}
        data-project-list-empty=""
        sx={{
          height: `calc(100% - ${AppBarStyles.HEIGHT * 4}px)`,
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
          background: 'transparent',
        }}
      >
        <Box
          className={classes.emptyState}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' },
          }}
        >
          <CreateProjectMenu
            buttonSize="large"
            buttonColor="neutral"
            buttonLabel="Create your first project"
          />
        </Box>
      </Box>
    );
  }
  if (!hasMatchingProjects) {
    return (
      <Box
        ref={contentRef}
        sx={{
          [ELEMENT_LIST_WIDTH_VAR]: `${listWidth}px`,
          height: `calc(100% - ${AppBarStyles.HEIGHT * 4}px)`,
          position: 'relative',
          zIndex: 1,
          pointerEvents: view === 'Map' ? 'none' : 'auto',
          '& > :first-of-type': { pointerEvents: 'auto' },
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          className={classes.emptyState}
          sx={{
            flex: 1,
            height: 'auto',
            position: 'relative',
            width: view === 'Map' ? ELEMENT_LIST_WIDTH_CSS : '100%',
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'absolute',
              top: '25%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'max-content',
              maxWidth: '100%',
              gap: 4,
            }}
          >
            <EmptyState message="No matching projects" />
            <GhostButton onClick={clearFilters}>Clear filter</GhostButton>
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box
      ref={contentRef}
      className={classes.list}
      sx={{
        [ELEMENT_LIST_WIDTH_VAR]: `${listWidth}px`,
        pointerEvents: view === 'Map' ? 'none' : 'auto',
        '--element-list-column-divider-shadow': 'none',
        '& [role="tree"] .element-list-resize-handle': { display: 'none' },
        ...(view === 'Map' && {
          [`.${ELEMENT_LIST_RESIZE_HANDLE_CLASS}`]: { display: 'none' },
        }),
        '&:has([data-project-updated-cell]:hover) [data-project-updated-value]':
          {
            visibility: 'visible',
          },
      }}
    >
      <Box
        sx={{
          background: 'transparent',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ProjectListTree
          items={sortedFilteredProjects}
          mapColumns={mapLayout.columns}
          mapWidth={mapLayout.width}
        />
        {view === 'List' && <Box sx={{ flex: 1 }} />}
      </Box>
      {view === 'List' && (
        <Box
          className={ELEMENT_LIST_RESIZE_HANDLE_CLASS}
          onMouseDown={handleDragStart}
          sx={{
            position: 'absolute',
            left: `calc(${ELEMENT_LIST_WIDTH_CSS} + ${PROJECT_LIST_SIDE_GUTTER}px - 4px)`,
            top: 9,
            bottom: 0,
            width: 4,
            zIndex: 2,
            cursor: 'ew-resize',
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
          }}
        />
      )}
    </Box>
  );
};

// Loading changes the surface, without rendering the retained card/list tree again.
const ProjectListOpeningSurface = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode }
>(({ children }, ref) => {
  const opening = useProjectOpening((state) => state.phase !== 'idle');
  return (
    <Box
      ref={ref}
      inert={opening}
      sx={{
        position: 'absolute',
        top: AppBarStyles.HEIGHT,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 1000,
        ...(opening && {
          pointerEvents: 'none',
          '& [role="tree"], & [data-project-list-header-sort], & [data-project-list-view-tabs]':
            { opacity: 0 },
        }),
      }}
    >
      {children}
    </Box>
  );
});
ProjectListOpeningSurface.displayName = 'ProjectListOpeningSurface';

const ProjectSelector: React.FC = () => {
  const { classes } = useStyles();
  const listReady = useDeferMount();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const setScrollElement = useCallback((element: HTMLDivElement | null) => {
    scrollRef.current = element;
    setScrollRoot(element);
  }, []);
  useLayoutEffect(() => {
    if (!scrollRoot) return;
    const root = document.documentElement;
    // Let the browser sample scroll and header geometry together. The fixed
    // scrollport and spacer keep the animation from changing its own input.
    const nativeScrollAnimation =
      CSS.supports('animation-timeline', '--project-list-scroll') &&
      CSS.supports('timeline-scope', '--project-list-scroll') &&
      CSS.supports('animation-range', '0px 192px');
    const updateHeaderHeight = () => {
      const extraHeight = Math.max(
        0,
        AppBarStyles.HEIGHT * 4 - scrollRoot.scrollTop,
      );
      root.style.setProperty(
        '--project-list-app-header-height',
        `${AppBarStyles.HEIGHT + extraHeight}px`,
      );
      root.style.setProperty(
        '--project-list-sort-visibility',
        extraHeight > 24 ? 'visible' : 'hidden',
      );
    };
    if (!nativeScrollAnimation) {
      updateHeaderHeight();
      scrollRoot.addEventListener('scroll', updateHeaderHeight, {
        passive: true,
      });
    }
    const header = document.querySelector<HTMLElement>('[data-app-header]');
    const scrollFromHeader = (event: WheelEvent) => {
      if (event.ctrlKey || !event.deltaY) return;
      const deltaScale =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? scrollRoot.clientHeight
            : 1;
      event.preventDefault();
      scrollRoot.scrollTop += event.deltaY * deltaScale;
    };
    header?.addEventListener('wheel', scrollFromHeader, { passive: false });
    return () => {
      header?.removeEventListener('wheel', scrollFromHeader);
      scrollRoot.removeEventListener('scroll', updateHeaderHeight);
      root.style.removeProperty('--project-list-app-header-height');
      root.style.removeProperty('--project-list-sort-visibility');
    };
  }, [scrollRoot]);
  const [rowBufferHeight, setRowBufferHeight] = useState(window.innerHeight);
  useEffect(() => {
    if (!scrollRoot) return;
    // Keep a viewport of rows on either side mounted during scrolling
    // and short reverse scrolls, instead of repeatedly rebuilding their menus.
    const update = () => setRowBufferHeight(scrollRoot.clientHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(scrollRoot);
    return () => observer.disconnect();
  }, [scrollRoot]);
  const [, setProjectSearchString] = useProjectSearchString();
  useEffect(() => {
    setProjectSearchString('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isProjectList = useIsProjectList();

  const closeProjectSelector = useCallback(() => {
    if (isProjectList) return;
  }, [isProjectList]);

  useKeydownListener(closeProjectSelector, 'Escape');

  return (
    <ProjectListOpeningSurface ref={wrapperRef}>
      <Box
        className={classes.container}
        data-native-overflow=""
        data-project-list-page=""
        ref={setScrollElement}
      >
        <GlobalStyles
          styles={(theme) => ({
            '@property --project-list-app-header-height': {
              syntax: '"<length>"',
              inherits: 'true',
              initialValue: '240px',
            },
            '@keyframes projectListHeaderCollapse': {
              from: { '--project-list-app-header-height': '240px' },
              to: { '--project-list-app-header-height': '48px' },
            },
            '@keyframes projectListSortVisibility': {
              '0%, 87.5%': { visibility: 'visible' },
              '87.5001%, 100%': { visibility: 'hidden' },
            },
            '@supports (animation-timeline: --project-list-scroll) and (timeline-scope: --project-list-scroll) and (animation-range: 0px 192px)':
              {
                ':root': { timelineScope: '--project-list-scroll' },
                '[data-project-list-page]': {
                  scrollTimelineName: '--project-list-scroll',
                  scrollTimelineAxis: 'block',
                },
                '[data-app-header]': {
                  animation: 'projectListHeaderCollapse 1s linear both',
                  animationTimeline: '--project-list-scroll',
                  animationRange: '0px 192px',
                },
                '[data-project-list-header-sort]': {
                  animation: 'projectListSortVisibility 1s linear both',
                  animationTimeline: '--project-list-scroll',
                  animationRange: '0px 192px',
                },
              },
            'html:has([data-project-list-page]), body:has([data-project-list-page]), #root:has([data-project-list-page]), div:has(> [data-project-list-page])':
              { backgroundColor: theme.palette.warmSurface.dark },
          })}
        />
        <Box
          aria-hidden="true"
          data-project-list-header-spacer=""
          sx={{ height: AppBarStyles.HEIGHT * 4, pointerEvents: 'none' }}
        />
        <ProjectListViewProvider>
          <ProjectListViewTabs />
          <ProjectListMap contentReady={listReady}>
            {listReady ? (
              <SharedIntersectionObserverProvider
                root={scrollRoot}
                rootMargin={`${rowBufferHeight}px 0px`}
              >
                <ListColumnResizeProvider value={projectListColumnResize}>
                  <ProjectSelectorContent />
                </ListColumnResizeProvider>
              </SharedIntersectionObserverProvider>
            ) : null}
          </ProjectListMap>
        </ProjectListViewProvider>
      </Box>
      <VerticalFadeScrollbar scrollRef={scrollRef} wrapperRef={wrapperRef} />
    </ProjectListOpeningSurface>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    overflowAnchor: 'none',
    backgroundColor: theme.palette.warmSurface.dark,
  },
  list: {
    position: 'relative',
    zIndex: 1,
    minHeight: `calc(100% - ${AppBarStyles.HEIGHT * 4}px)`,
    display: 'flex',
    flexDirection: 'column',
    '& > *': { flexShrink: 0 },
    containerName: PROJECT_LIST_CONTAINER_NAME,
    containerType: 'inline-size',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
}));

export default React.memo(ProjectSelector);
